import { Controller } from "@hotwired/stimulus"
import { Chart, RadarController, LineController, BarController, DoughnutController, PolarAreaController, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// Register Chart.js components
Chart.register(
  RadarController,
  LineController,
  BarController,
  DoughnutController,
  PolarAreaController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  ChartDataLabels
)

// Hiring Manager Profile Visualization Controller
export default class extends Controller {
  static targets = [
    "leadershipStyleRadar",
    "leadershipStyleBars",
    "profileCompletionChart",
    "profileTimeline",
    "styleContextChart"
  ]

  static values = {
    styleValues: Object,
    completionPercentage: Number,
    completionBySection: Object,
    versionDates: Array,
    versionValues: Array,
    contextualAdaptability: Object
  }

  connect() {
    this.initializeCharts()
  }

  initializeCharts() {
    if (this.hasLeadershipStyleRadarTarget) {
      this.initLeadershipStyleRadar()
    }

    if (this.hasLeadershipStyleBarsTarget) {
      this.initLeadershipStyleBars()
    }

    if (this.hasProfileCompletionChartTarget) {
      this.initProfileCompletionChart()
    }

    if (this.hasProfileTimelineTarget && this.hasVersionDatesValue && this.hasVersionValuesValue) {
      this.initProfileTimeline()
    }

    if (this.hasStyleContextChartTarget && this.hasContextualAdaptabilityValue) {
      this.initStyleContextChart()
    }
  }

  // Leadership Style Radar Chart
  initLeadershipStyleRadar() {
    const ctx = this.leadershipStyleRadarTarget.getContext('2d')
    const styleValues = this.styleValuesValue || {
      directive: 0,
      supportive: 0,
      participative: 0,
      achievement_oriented: 0
    }

    this.leadershipRadarChart = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: [
          'Directive',
          'Supportive',
          'Participative',
          'Achievement-Oriented'
        ],
        datasets: [{
          label: 'Leadership Style',
          data: [
            styleValues.directive || 0,
            styleValues.supportive || 0,
            styleValues.participative || 0,
            styleValues.achievement_oriented || 0
          ],
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(54, 162, 235, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
        }]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100
          }
        },
        plugins: {
          datalabels: {
            backgroundColor: function(context) {
              return context.dataset.backgroundColor;
            },
            borderRadius: 4,
            color: 'white',
            font: {
              weight: 'bold'
            },
            formatter: Math.round,
            padding: 6
          },
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed !== null) {
                  label += context.parsed;
                }
                return label;
              }
            }
          }
        }
      }
    })
  }

  // Leadership Style Bar Chart
  initLeadershipStyleBars() {
    const ctx = this.leadershipStyleBarsTarget.getContext('2d')
    const styleValues = this.styleValuesValue || {
      directive: 0,
      supportive: 0,
      participative: 0,
      achievement_oriented: 0
    }

    const styles = [
      { name: 'Directive', value: styleValues.directive || 0, color: 'rgba(255, 99, 132, 0.8)' },
      { name: 'Supportive', value: styleValues.supportive || 0, color: 'rgba(54, 162, 235, 0.8)' },
      { name: 'Participative', value: styleValues.participative || 0, color: 'rgba(75, 192, 192, 0.8)' },
      { name: 'Achievement-Oriented', value: styleValues.achievement_oriented || 0, color: 'rgba(255, 206, 86, 0.8)' }
    ]

    // Sort styles by value in descending order
    styles.sort((a, b) => b.value - a.value)

    this.leadershipBarChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: styles.map(s => s.name),
        datasets: [{
          label: 'Leadership Styles',
          data: styles.map(s => s.value),
          backgroundColor: styles.map(s => s.color),
          borderColor: styles.map(s => s.color.replace('0.8', '1')),
          borderWidth: 1
        }]
      },
      options: {
        indexAxis: 'y',
        plugins: {
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold'
            },
            formatter: Math.round
          },
          legend: {
            display: false
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            suggestedMax: 100
          }
        }
      }
    })
  }

  // Profile Completion Doughnut Chart
  initProfileCompletionChart() {
    const ctx = this.profileCompletionChartTarget.getContext('2d')
    const completion = this.completionPercentageValue || 0
    
    this.completionChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Completed', 'Remaining'],
        datasets: [{
          data: [completion, 100 - completion],
          backgroundColor: [
            'rgba(75, 192, 192, 0.8)',
            'rgba(220, 220, 220, 0.8)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(220, 220, 220, 1)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        cutout: '75%',
        plugins: {
          datalabels: {
            color: function(context) {
              return context.dataIndex === 0 ? '#fff' : '#666';
            },
            font: {
              weight: 'bold',
              size: 16
            },
            formatter: function(value, context) {
              return context.dataIndex === 0 ? value + '%' : '';
            }
          },
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.label + ': ' + context.parsed + '%';
              }
            }
          }
        }
      }
    })

    // Add center text for completion percentage
    Chart.register({
      id: 'centerText',
      beforeDraw: function(chart) {
        if (chart.config.type === 'doughnut') {
          // Get ctx from chart
          const ctx = chart.ctx
          
          // Get data
          const completion = chart.data.datasets[0].data[0]
          
          // Get inner center coordinates and radius
          const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2)
          const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2)
          
          // Text settings
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          
          // Draw completion percentage
          ctx.font = '700 24px Arial'
          ctx.fillStyle = '#333'
          ctx.fillText(`${Math.round(completion)}%`, centerX, centerY)
          
          // Draw label underneath
          ctx.font = '14px Arial'
          ctx.fillStyle = '#666'
          ctx.fillText('Complete', centerX, centerY + 25)
        }
      }
    })
  }

  // Profile Timeline Chart
  initProfileTimeline() {
    const ctx = this.profileTimelineTarget.getContext('2d')
    const dates = this.versionDatesValue || []
    const values = this.versionValuesValue || []

    this.timelineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [{
          label: 'Profile Changes Over Time',
          data: values,
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1,
          fill: true
        }]
      },
      options: {
        responsive: true,
        plugins: {
          datalabels: {
            display: false
          },
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return 'Version: ' + context.parsed.y;
              }
            }
          }
        },
        scales: {
          y: {
            title: {
              display: true,
              text: 'Version'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Date'
            }
          }
        }
      }
    })
  }

  // Contextual Leadership Style Chart
  initStyleContextChart() {
    const ctx = this.styleContextChartTarget.getContext('2d')
    const adaptability = this.contextualAdaptabilityValue || {}
    
    // Default contexts if not provided
    const contexts = Object.keys(adaptability).length > 0 ? Object.keys(adaptability) : [
      'New Team', 'Crisis', 'Routine', 'Creative', 'Experience', 'Stress'
    ]
    
    // Default values if not provided (equal values)
    const defaultStyle = { directive: 25, supportive: 25, participative: 25, achievement_oriented: 25 }
    
    // Prepare data for each context
    const datasets = [
      {
        label: 'Directive',
        data: contexts.map(context => (adaptability[context]?.directive || defaultStyle.directive)),
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      },
      {
        label: 'Supportive',
        data: contexts.map(context => (adaptability[context]?.supportive || defaultStyle.supportive)),
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'Participative',
        data: contexts.map(context => (adaptability[context]?.participative || defaultStyle.participative)),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      },
      {
        label: 'Achievement-Oriented',
        data: contexts.map(context => (adaptability[context]?.achievement_oriented || defaultStyle.achievement_oriented)),
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      }
    ]

    this.contextChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: contexts,
        datasets: datasets
      },
      options: {
        plugins: {
          datalabels: {
            display: false
          }
        },
        responsive: true,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: 100
          }
        }
      }
    })
  }

  // Lifecycle methods to clean up charts when controller disconnects
  disconnect() {
    if (this.leadershipRadarChart) {
      this.leadershipRadarChart.destroy()
    }
    if (this.leadershipBarChart) {
      this.leadershipBarChart.destroy()
    }
    if (this.completionChart) {
      this.completionChart.destroy()
    }
    if (this.timelineChart) {
      this.timelineChart.destroy()
    }
    if (this.contextChart) {
      this.contextChart.destroy()
    }
  }
}